import { useState } from 'react';
import { jsPDF } from 'jspdf';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, ChevronDown, CheckCircle2, X, Loader2, Upload, FileText, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import SignaturePad from './SignaturePad';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const labelCls = "text-xs text-muted-foreground block mb-1";

const CONTACT_METHODS = ['Phone', 'Fax', 'Email', 'Letter', 'Certified Mail'];
const GF_REASONS = [
  'Previous Employer is no longer in business',
  'Unable to locate Previous Employer',
  'Previous Employer refused to release information',
  'Other',
];

const statusColor = (status) => {
  if (status === 'Manually Completed') return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (status === 'N/A') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  if (status === 'Good Faith Completed') return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
  if (status === 'Sent Letter' || status === 'Called') return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  return 'bg-muted/40 text-muted-foreground border-border';
};

const emptyEmployer = () => ({
  id: Date.now() + Math.random(),
  name: '',
  contact: '',
  phone: '',
  fax: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  position: '',
  reason_left: '',
  date_from: '',
  date_to: '',
  is_present: false,
  dot_regulated: 'Yes',
  safety_sensitive: 'Yes',
  gaps: '',
  notes: '',
  documents: [],
  status: 'Pending',
  completed_date: '',
  good_faith_url: '',
});

// ── Good Faith PDF Generator ──────────────────────────────────────────────────
const generateEmployerGoodFaithPdf = async (formData, employer, driver) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = 612, ml = 50, mr = 562;
  let y = 50;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 792, 'F');
  doc.setTextColor(0, 0, 0);

  // Letterhead
  doc.setFontSize(12); doc.setFont('helvetica', 'bold');
  doc.text('GF Trucklines LLC', ml, y);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('GF Trucklines LLC', mr, y, { align: 'right' }); y += 12;
  doc.text('6041 Thorne Bay Ct', mr, y, { align: 'right' }); y += 12;
  doc.text('Las Vegas, NV 89110', mr, y, { align: 'right' }); y += 12;
  doc.text('(702) 601-8300', mr, y, { align: 'right' }); y += 22;

  // Title
  doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text('WRITTEN RECORD OF GOOD FAITH EFFORT INQUIRY INTO PREVIOUS EMPLOYER', W / 2, y, { align: 'center' }); y += 14;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('In accordance with 49 CFR PARTS 40.25/382.413/391.23', W / 2, y, { align: 'center' }); y += 22;

  const section = (title) => {
    doc.setFillColor(220, 220, 220);
    doc.rect(ml, y - 11, mr - ml, 16, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(title, ml + 4, y);
    doc.setFont('helvetica', 'normal');
    y += 18;
  };

  // APPLICANT'S INFORMATION
  section("APPLICANT'S INFORMATION");
  const ssn = driver.ein ? '***-**-' + String(driver.ein).slice(-4) : '—';
  const dob = driver.dob ? new Date(driver.dob + 'T00:00:00').toLocaleDateString('en-US') : '—';
  doc.setFontSize(9);
  doc.text(`Name: ${driver.full_name || '—'}`, ml, y);
  doc.text(`SSN: ${ssn}`, ml + 200, y);
  doc.text(`DOB: ${dob}`, ml + 360, y);
  y += 22;

  // PREVIOUS EMPLOYER INFORMATION
  section('PREVIOUS EMPLOYER INFORMATION');
  doc.text(`Company: ${employer.name || '—'}`, ml, y);
  doc.text(`Phone: ${employer.phone || '—'}`, ml + 230, y);
  doc.text(`Fax: ${employer.fax || '—'}`, ml + 380, y);
  y += 14;
  doc.text(`Address: ${employer.address || '—'}`, ml, y);
  doc.text(`City: ${employer.city || '—'}`, ml + 200, y);
  doc.text(`State: ${employer.state || '—'}`, ml + 320, y);
  doc.text(`Postal Code: ${employer.zip || '—'}`, ml + 380, y);
  y += 22;

  // GOOD FAITH QUESTIONNAIRE
  section('GOOD FAITH QUESTIONNAIRE');
  const contactDate = formData.contact_date
    ? new Date(formData.contact_date + 'T00:00:00').toLocaleDateString('en-US')
    : '—';
  doc.text(`Date Contact Attempted: ${contactDate}`, ml, y);
  doc.text(`Contact Method: ${formData.contact_method || '—'}`, ml + 260, y);
  y += 20;

  GF_REASONS.forEach(r => {
    const selected = formData.reason === r;
    doc.setDrawColor(0, 0, 0);
    doc.circle(ml + 5, y - 3, 4, 'S');
    if (selected) { doc.setFillColor(0, 0, 0); doc.circle(ml + 5, y - 3, 2.5, 'F'); }
    doc.setFillColor(0, 0, 0);
    doc.setFont('helvetica', selected ? 'bold' : 'normal');
    doc.text(r, ml + 16, y);
    y += 16;
    if (r === 'Other' && selected && formData.other_explain) {
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(formData.other_explain, mr - ml - 20);
      doc.text(lines, ml + 16, y);
      y += lines.length * 13;
    }
  });
  y += 10;

  // NOTES
  section('NOTES');
  if (formData.notes) {
    const lines = doc.splitTextToSize(formData.notes, mr - ml);
    doc.setFont('helvetica', 'normal');
    doc.text(lines, ml, y);
    y += lines.length * 13 + 10;
  } else {
    y += 50;
  }

  // Signature
  y += 10;
  if (formData.signature_data?.startsWith('data:')) {
    try { doc.addImage(formData.signature_data, 'PNG', ml, y, 120, 50); } catch {}
  }
  const signDate = contactDate;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(signDate, ml + 340, y + 40);
  y += 58;
  doc.line(ml, y, ml + 200, y);
  doc.line(ml + 300, y, mr, y);
  doc.setFontSize(8);
  doc.text('Conducted By', ml, y + 10);
  if (formData.conducted_by) doc.text(formData.conducted_by, ml, y - 4);
  doc.text('Date Signed', ml + 300, y + 10);

  // Footer
  const today = new Date().toLocaleDateString('en-US');
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(today, ml, 775);
  doc.text('GF Trucklines LLC', W / 2, 775, { align: 'center' });
  doc.text('Page 1', mr, 775, { align: 'right' });

  const blob = doc.output('blob');
  const file = new File([blob], `good_faith_${(employer.name || 'employer').replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
  const { file_url } = await client.integrations.Core.UploadFile({ file });
  return file_url;
};

// ── Good Faith Modal ──────────────────────────────────────────────────────────
function GoodFaithEmployerModal({ employer, driver, onClose, onComplete }) {
  const [form, setForm] = useState({
    contact_date: new Date().toISOString().split('T')[0],
    contact_method: 'Phone',
    reason: '',
    other_explain: '',
    notes: '',
    conducted_by: '',
    signature_data: '',
  });
  const [generating, setGenerating] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleGenerate = async () => {
    if (!form.contact_date || !form.reason || !form.signature_data) {
      toast.error('Date, reason, and signature are required.');
      return;
    }
    setGenerating(true);
    toast.info('Generating Good Faith document…');
    try {
      const url = await generateEmployerGoodFaithPdf(form, employer, driver);
      toast.success('Good Faith document generated.');
      onComplete(url);
    } catch (err) {
      toast.error('Failed to generate: ' + (err?.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-xl z-10 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="font-space font-bold text-foreground text-base">Good Faith Effort — Previous Employer</h2>
            <p className="text-xs text-muted-foreground mt-0.5">49 CFR Parts 40.25 / 382.413 / 391.23</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground ml-3 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* Employer summary */}
          <div className="px-3 py-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-0.5">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest">Previous Employer</p>
            <p className="text-foreground font-medium">{employer.name || '—'}</p>
            <p className="text-muted-foreground">
              {[employer.address, employer.city, employer.state, employer.zip].filter(Boolean).join(', ') || '—'}
            </p>
            {(employer.phone || employer.fax) && (
              <p className="text-muted-foreground">
                {employer.phone ? `Phone: ${employer.phone}` : ''}
                {employer.fax ? `  ·  Fax: ${employer.fax}` : ''}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Date Contact Attempted <span className="text-red-400">*</span></label>
              <input type="date" value={form.contact_date} onChange={e => set('contact_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact Method <span className="text-red-400">*</span></label>
              <select value={form.contact_method} onChange={e => set('contact_method', e.target.value)} className={inputCls}>
                {CONTACT_METHODS.map(m => <option key={m} value={m} className="bg-background">{m}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls + ' mb-2'}>Reason <span className="text-red-400">*</span></label>
            <div className="space-y-2.5">
              {GF_REASONS.map(r => (
                <label key={r} onClick={() => set('reason', r)} className="flex items-center gap-2.5 cursor-pointer group">
                  <div className={cn(
                    "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                    form.reason === r ? 'border-primary bg-primary' : 'border-border group-hover:border-border'
                  )}>
                    {form.reason === r && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
                  </div>
                  <span className="text-sm text-foreground">{r}</span>
                </label>
              ))}
            </div>
          </div>

          {form.reason === 'Other' && (
            <div>
              <label className={labelCls}>Explain:</label>
              <textarea value={form.other_explain} onChange={e => set('other_explain', e.target.value)} rows={2} className={inputCls + ' resize-none'} />
            </div>
          )}

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inputCls + ' resize-none'} />
          </div>

          <div>
            <label className={labelCls}>Conducted By <span className="text-red-400">*</span></label>
            <input value={form.conducted_by} onChange={e => set('conducted_by', e.target.value)} className={inputCls} placeholder="Full name of person completing this form" />
          </div>

          <div>
            <label className={labelCls}>Signature <span className="text-red-400">*</span></label>
            <SignaturePad value={form.signature_data} onChange={v => set('signature_data', v)} />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-3 flex-shrink-0">
          <Button variant="outline" className="border-border h-8 px-4 text-xs" onClick={onClose}>Cancel</Button>
          <Button className="bg-primary text-primary-foreground h-8 px-4 text-xs" onClick={handleGenerate} disabled={generating}>
            {generating
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</>
              : 'Generate & Save Document'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function EmployerDetailModal({ employer, onClose, onUpdate }) {
  const [form, setForm] = useState({ ...employer });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    toast.info('Uploading document...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setForm(p => ({ ...p, documents: [...(p.documents || []), { name: file.name, url: file_url }] }));
    setUploading(false);
    toast.success('Document uploaded.');
  };

  const removeDoc = (idx) => setForm(p => ({ ...p, documents: p.documents.filter((_, i) => i !== idx) }));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-lg z-10 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="font-space font-bold text-foreground text-base">View / Update Previous Employer Information</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground ml-3 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-3 flex-1 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Employer Name <span className="text-red-400">*</span></label>
              <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} placeholder="G&f Trucklines Llc" />
            </div>
            <div>
              <label className={labelCls}>Contact Person <span className="text-red-400">*</span></label>
              <input value={form.contact} onChange={e => set('contact', e.target.value)} className={inputCls} placeholder="Juan Fuentes" />
            </div>
          </div>

          <div>
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => {
                set('address', data.street || form.address);
                if (data.city) set('city', data.city);
                if (data.state) set('state', data.state);
                if (data.zip) set('zip', data.zip);
              }}
              label="Address *"
              placeholder="6041 THORNE BAY CT"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>City <span className="text-red-400">*</span></label>
              <input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} placeholder="LAS VEGAS" />
            </div>
            <div>
              <label className={labelCls}>State <span className="text-red-400">*</span></label>
              <select value={form.state} onChange={e => set('state', e.target.value)} className={inputCls}>
                <option value="" className="bg-background">NV</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Zipcode <span className="text-red-400">*</span></label>
              <input value={form.zip} onChange={e => set('zip', e.target.value)} className={inputCls} placeholder="89110" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Phone <span className="text-red-400">*</span></label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="(702)601-8300" />
            </div>
            <div>
              <label className={labelCls}>Fax:</label>
              <input value={form.fax} onChange={e => set('fax', e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Email:</label>
              <input value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} placeholder="example@email.com" />
            </div>
            <div>
              <label className={labelCls}>Position:</label>
              <input value={form.position} onChange={e => set('position', e.target.value)} className={inputCls} placeholder="Manager/driver" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Reason Left:</label>
              <input value={form.reason_left} onChange={e => set('reason_left', e.target.value)} className={inputCls} placeholder="Closing" />
            </div>
            <div>
              <label className={labelCls}>From:</label>
              <input value={form.date_from} onChange={e => set('date_from', e.target.value)} className={inputCls} placeholder="03/2025" />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className={labelCls + ' mb-0'}>To:</label>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={form.is_present} onChange={e => set('is_present', e.target.checked)} className="rounded" />
                Present
              </label>
            </div>
            {!form.is_present && (
              <input value={form.date_to} onChange={e => set('date_to', e.target.value)} className={inputCls} placeholder="02/2026" />
            )}
          </div>

          <div>
            <label className={labelCls}>Were you subject to the DOT/FMCSA regulations while employed by this carrier? <strong className="text-foreground">If you were a driver, choose Yes. <span className="text-red-400">*</span></strong></label>
            <select value={form.dot_regulated} onChange={e => set('dot_regulated', e.target.value)} className={inputCls}>
              <option value="Yes" className="bg-background">Yes</option>
              <option value="No" className="bg-background">No</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Was your job designated as a safety sensitive function, in any DOT regulated mode, subject to the alcohol and controlled substances testing requirements required by 49 CFR Part 40? <strong className="text-foreground">If you were a driver, choose Yes. <span className="text-red-400">*</span></strong></label>
            <select value={form.safety_sensitive} onChange={e => set('safety_sensitive', e.target.value)} className={inputCls}>
              <option value="Yes" className="bg-background">Yes</option>
              <option value="No" className="bg-background">No</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Explain any gaps in employment if in excess of one (1) month. Include month/year and reason.</label>
            <textarea value={form.gaps} onChange={e => set('gaps', e.target.value)} rows={3} className={inputCls + ' resize-none'} />
          </div>

          <div>
            <label className={labelCls}>Notes:</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inputCls + ' resize-none'} />
          </div>

          {/* Documents */}
          <div>
            <label className={labelCls}>Uploaded Documents:</label>
            {(form.documents || []).length === 0 && (
              <p className="text-xs text-muted-foreground">No documents uploaded.</p>
            )}
            <div className="space-y-1 mb-2">
              {(form.documents || []).map((doc, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline truncate flex-1">{doc.name}</a>
                  <button onClick={() => removeDoc(idx)} className="text-red-400/60 hover:text-red-400"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:border-primary/40 cursor-pointer transition-colors">
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
              {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Upload Additional Documents</>}
            </label>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-3 flex-shrink-0">
          <Button variant="outline" className="border-border h-8 px-4 text-xs" onClick={onClose}>Close</Button>
          <Button className="bg-primary text-primary-foreground h-8 px-4 text-xs" onClick={() => { onUpdate(form); onClose(); }}>
            Update
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PreviousEmploymentSection({ driver, onComplete }) {
  const [employers, setEmployers] = useState(() => {
    try {
      const saved = driver.prev_employer_data ? JSON.parse(driver.prev_employer_data) : null;
      return saved && saved.length > 0 ? saved : [emptyEmployer()];
    } catch { return [emptyEmployer()]; }
  });
  const [openAction, setOpenAction] = useState(null);
  const [detailEmp, setDetailEmp] = useState(null);
  const [goodFaithEmp, setGoodFaithEmp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);

  const addEmployer = () => {
    const newEmp = emptyEmployer();
    setEmployers(prev => [...prev, newEmp]);
    setDetailEmp(newEmp);
    setAddingNew(true);
  };
  const removeEmployer = (id) => setEmployers(prev => prev.filter(e => e.id !== id));

  // Persists immediately rather than only updating local state -- the
  // status quick-actions and "Update" in the detail modal used to just set
  // local employers state and rely on a separate outer "Save" click to
  // actually persist dqf_prev_employer_verification. That second click was
  // easy to miss (the detail modal's own "Update" button reads like a save
  // action on its own), so a driver's employer info and completion status
  // could look done in the table but never actually reach the database --
  // exactly what the Good Faith flow already avoided by calling handleSave
  // itself. Every mutation here now does the same.
  const updateEmployer = (updated) => {
    const next = employers.map(e => e.id === updated.id ? updated : e);
    setEmployers(next);
    handleSave(next);
  };

  const setStatus = (id, status) => {
    const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    const next = employers.map(e => e.id === id ? { ...e, status, completed_date: today } : e);
    setEmployers(next);
    setOpenAction(null);
    handleSave(next);
  };

  const handleSave = async (overrideEmployers) => {
    setSaving(true);
    const data = overrideEmployers || employers;
    // Blank, never-touched placeholder rows (e.g. the default stub the
    // form starts with) shouldn't block completion just because they
    // exist -- only rows the driver actually put something into or
    // acted on (a name/contact, or any non-default status) need to be
    // resolved. Without this, an untouched stub row left behind after
    // adding real employers would keep dqf_prev_employer_verification
    // false forever even once every real employer was resolved.
    const relevant = data.filter(e => e.name || e.contact || e.status !== 'Pending');
    const allDone = relevant.length > 0 && relevant.every(e => e.status === 'Manually Completed' || e.status === 'N/A' || e.status === 'Good Faith Completed');
    await client.entities.DriverProfile.update(driver.id, {
      prev_employer_data: JSON.stringify(data),
      dqf_prev_employer_verification: allDone,
    });
    toast.success('Previous employment saved.');
    setSaving(false);
    if (allDone && onComplete) onComplete();
  };

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="space-y-2">
        <p className="text-xs text-foreground leading-relaxed">
          Carriers must investigate the driver's employment record during the{' '}
          <span className="text-red-400 font-semibold">preceding three years.</span>{' '}
          This investigation must be completed within 30 days of the date employment begins. Carrier must retain a record of the request and all response documentation.
        </p>
        <p className="text-xs text-red-400 font-semibold">
          If the date of previous employment is NOT WITHIN proceeding 3 years then you may "Mark as not required"
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          This qualification requires that you perform a safety performance, alcohol and drug investigation with each of the driver's previous employers from the last 3 years that were subject to FMCSA regulations.
        </p>
      </div>

      {/* Good Faith callout */}
      <div className="border-l-4 border-amber-500 bg-amber-500/10 px-4 py-3 rounded-r-lg">
        <p className="text-xs text-amber-300 leading-relaxed">
          <strong>The Good Faith Effort Previous Employer History Investigation is only required when you have attempted to obtain the previous employer investigation documents and was unsuccessful for any reason.</strong>
          {' '}
          <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391#391.23" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
            FMCSA 49 CFR Part 391.23
          </a>
        </p>
      </div>

      {/* Employer table */}
      <div className="rounded-lg border border-border overflow-visible text-xs">
        <div className="grid grid-cols-4 bg-white/5 px-3 py-2 font-semibold text-muted-foreground">
          <span>Employer</span>
          <span>Contact</span>
          <span>Status</span>
          <span>Action</span>
        </div>

        {employers.map((emp) => (
          <div key={emp.id} className="border-t border-border grid grid-cols-4 px-3 py-2.5 gap-2 items-center">
            <span className="text-foreground truncate">{emp.name || <span className="text-muted-foreground italic">—</span>}</span>
            <span className="text-muted-foreground truncate">{emp.contact || '—'}</span>
            <div>
              {emp.status !== 'Pending' ? (
                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium', statusColor(emp.status))}>
                  <CheckCircle2 className="w-3 h-3" />
                  {emp.status}{emp.completed_date && ` ✓ ${emp.completed_date}`}
                </span>
              ) : (
                <span className="text-muted-foreground italic">Pending</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenAction(openAction === emp.id ? null : emp.id)}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-foreground transition-colors"
                >
                  Action <ChevronDown className="w-3 h-3" />
                </button>
                {openAction === emp.id && (
                  <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl min-w-[230px] py-1">
                    <button
                      type="button"
                      onClick={() => { setOpenAction(null); setDetailEmp(emp); }}
                      className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors"
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => { setOpenAction(null); setDetailEmp({ ...emp, _focus: 'notes' }); }}
                      className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors"
                    >
                      Notes
                    </button>

                    {/* Good Faith */}
                    <div className="border-t border-border my-1" />
                    <button
                      type="button"
                      onClick={() => { setOpenAction(null); setGoodFaithEmp(emp); }}
                      className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors"
                    >
                      Complete Good Faith Document
                    </button>
                    {emp.good_faith_url && (
                      <a
                        href={emp.good_faith_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpenAction(null)}
                        className="w-full text-left px-4 py-2 text-xs text-purple-400 hover:bg-purple-500/10 transition-colors flex items-center gap-1.5"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Good Faith Document
                      </a>
                    )}

                    {/* File upload */}
                    <div className="border-t border-border my-1" />
                    <label className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors cursor-pointer flex items-center">
                      <input type="file" className="hidden" onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setOpenAction(null);
                        toast.info('Uploading...');
                        const { file_url } = await client.integrations.Core.UploadFile({ file });
                        setEmployers(prev => prev.map(x => x.id === emp.id
                          ? { ...x, documents: [...(x.documents || []), { name: file.name, url: file_url }] }
                          : x
                        ));
                        toast.success('Document uploaded.');
                      }} />
                      Upload Additional Documents
                    </label>

                    {/* Status shortcuts */}
                    <div className="border-t border-border my-1" />
                    <button
                      type="button"
                      onClick={() => setStatus(emp.id, 'Called')}
                      className="w-full text-left px-4 py-2 text-xs text-amber-400 hover:bg-amber-500/10 transition-colors"
                    >
                      Mark as Called
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(emp.id, 'Sent Letter')}
                      className="w-full text-left px-4 py-2 text-xs text-amber-400 hover:bg-amber-500/10 transition-colors"
                    >
                      Mark as Sent Letter
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(emp.id, 'N/A')}
                      className="w-full text-left px-4 py-2 text-xs text-blue-400 hover:bg-blue-500/10 transition-colors"
                    >
                      Mark as Not Required (N/A)
                    </button>

                    {employers.length > 1 && (
                      <div className="border-t border-border mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() => { removeEmployer(emp.id); setOpenAction(null); }}
                          className="w-full text-left px-4 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          Remove Employer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={addEmployer}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Employer
        </button>
        <div className="flex items-center gap-2">
          <a
            href="https://www.fmcsa.dot.gov/registration/commercial-drivers-license"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-400 text-xs hover:bg-amber-500/10 transition-colors"
          >
            Need Help?
          </a>
          <button
            type="button"
            onClick={() => handleSave()}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Good Faith Modal */}
      {goodFaithEmp && (
        <GoodFaithEmployerModal
          employer={goodFaithEmp}
          driver={driver}
          onClose={() => setGoodFaithEmp(null)}
          onComplete={async (url) => {
            const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
            const updated = employers.map(e => e.id === goodFaithEmp.id
              ? { ...e, good_faith_url: url, status: 'Good Faith Completed', completed_date: today }
              : e
            );
            setEmployers(updated);
            setGoodFaithEmp(null);
            await handleSave(updated);
          }}
        />
      )}

      {/* Detail Modal */}
      {detailEmp && (
        <EmployerDetailModal
          employer={detailEmp}
          onClose={() => {
            if (addingNew) {
              setEmployers(prev => prev.filter(e => e.id !== detailEmp.id || e.name));
              setAddingNew(false);
            }
            setDetailEmp(null);
          }}
          onUpdate={(updated) => {
            const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
            const withStatus = updated.name
              ? { ...updated, status: 'Manually Completed', completed_date: updated.completed_date || today }
              : updated;
            updateEmployer(withStatus);
            setAddingNew(false);
            setDetailEmp(null);
          }}
        />
      )}
    </div>
  );
}
